import { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';

/**
 * Fetches a publicly-traded company's full daily adjusted-close price history
 * from the Flask `stock-chart/{ticker}/all/` endpoint (the same source used by
 * StockChartContainer). Results are cached per-URL at module scope so flipping
 * an overlay toggle, changing chart buckets, or remounting never re-fetches.
 *
 * Returns { series, loading, error } where series is [{ date, adjusted_close }].
 * No-ops (empty series) when ticker is falsy — i.e. private companies.
 */

// key (full URL) -> Promise<series>
const seriesCache = new Map();

export function useStockSeries(ticker) {
    const { flaskServerAddress } = useAuth();
    const [state, setState] = useState({ series: [], loading: false, error: null });

    useEffect(() => {
        if (!ticker) {
            setState({ series: [], loading: false, error: null });
            return undefined;
        }

        let cancelled = false;
        const url = `${flaskServerAddress}stock-chart/${ticker}/all/`;
        setState((s) => ({ ...s, loading: true, error: null }));

        let promise = seriesCache.get(url);
        if (!promise) {
            promise = fetch(url)
                .then((r) => {
                    if (!r.ok) throw new Error('Failed to fetch stock data');
                    return r.json();
                })
                .then((json) => (Array.isArray(json.data) ? json.data : []))
                .catch((err) => {
                    // Don't cache failures — allow a later retry.
                    seriesCache.delete(url);
                    throw err;
                });
            seriesCache.set(url, promise);
        }

        promise
            .then((series) => {
                if (!cancelled) setState({ series, loading: false, error: null });
            })
            .catch((err) => {
                if (!cancelled) setState({ series: [], loading: false, error: err.message });
            });

        return () => {
            cancelled = true;
        };
    }, [ticker, flaskServerAddress]);

    return state;
}

export default useStockSeries;
